import React, {useCallback, useMemo} from "react";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, Box, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

export interface ImportCsvColumnOption {
    title: string;
}

export function toColumnOption(col: unknown): ImportCsvColumnOption | null {
    if (col != null && typeof col === 'object' && 'title' in (col as ImportCsvColumnOption)) {
        return col as ImportCsvColumnOption;
    }
    if (col == null || col === '') {
        return null;
    }
    return {title: String(col)};
}

interface ImportCsvColumnAutocompleteProps extends Omit<
    AutocompleteProps<ImportCsvColumnOption, false, false, false>,
    'renderInput' | 'renderOption' | 'options'
> {
    columns: string[];
    optional?: boolean;
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
}

const ImportCsvColumnAutocomplete = ({
                                         columns,
                                         optional = false,
                                         textFieldProps = {},
                                         form = false,
                                         name = undefined,
                                         required = false,
                                         ...props
                                     }: ImportCsvColumnAutocompleteProps) => {
    const columnOptions = useMemo(() => columns.map((title) => ({title})), [columns]);

    const getOptions = useCallback(async (_cancelToken: CancelToken, keywords?: string) => {
        const kw = (keywords ?? '').trim().toLowerCase();
        if (!kw) {
            return columnOptions;
        }
        return columnOptions.filter((option) => option.title.toLowerCase().includes(kw));
    }, [columnOptions]);

    const isOptionEqualToValue = useCallback((option: ImportCsvColumnOption, value: ImportCsvColumnOption | null) => (option?.title ?? '') === (value?.title ?? ''), [],);

    const getOptionLabel = useCallback((option: ImportCsvColumnOption) => option?.title ?? '', []);

    const getOptionKey = useCallback((option: ImportCsvColumnOption) => option?.title ?? '', []);

    const renderOption = useCallback((
        liProps: React.HTMLAttributes<HTMLLIElement>,
        option: ImportCsvColumnOption,
        _state: AutocompleteRenderOptionState,
    ) => (
        <Box
            component="li"
            {...liProps}
            sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                alignItems: 'flex-start',
                py: 1,
            }}
        >
            {option.title}
        </Box>
    ), []);

    return (
        <AutocompleteBase<ImportCsvColumnOption>
            keywordsBased={false}
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={0}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            renderOption={renderOption}
            options={[]}
            clearOnBlur={false}
            disableClearable={!optional as unknown as false}
            dependencies={[columns]}
            size="small"
            {...props}
        />
    );
};

export default ImportCsvColumnAutocomplete;
